// src/pages/Calibration/CalibrationCatalog.jsx
//
// AISC §1.14 Calibration Phase 1 — per-tenant instrument type catalog.
// Tenant-flexibility canon: ABC's catalog ≠ Chesakl's catalog.

import React, { useEffect, useState, useCallback } from "react";
import PageGuide from "../../components/PageGuide";
import {
  Container, Stack, Typography, Breadcrumbs, Button, Paper, Box, Alert,
  TableHead, TableRow, TableCell, TableBody, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, Select, MenuItem, Checkbox, FormControlLabel,
  CircularProgress, IconButton,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "react-toastify";
import { Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material";

import { useSelector } from "react-redux";

import { calibrationInstrumentsApi, CATEGORY_LABELS } from "../../utils/calibrationApi";
import { hasPermission, PERMISSIONS } from "../../utils/hasPermission";

import SmartTable from "../../components/SmartTable/SmartTable";
const CATEGORY_OPTIONS = Object.entries(CATEGORY_LABELS);

const EMPTY_FORM = {
  name: "",
  category: "general",
  default_frequency_days: "",
  is_transfer_gage: false,
  requires_external_lab: false,
  tolerance_text: "",
  notes: "",
};

export default function CalibrationCatalog() {
  // S-61 (2026-10-06): add, edit and delete are shown only with manage_calibration_catalog
  // (Admin and Quality Manager). The server refuses these writes without it (403).
  const { loginUser } = useSelector((state) => state.userSlice);
  const userPermissions = loginUser?.decodedToken?.permissions || [];
  const canManage = hasPermission(userPermissions, PERMISSIONS.MANAGE_CALIBRATION_CATALOG);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await calibrationInstrumentsApi.list();
      setRows(res?.data?.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  function openCreate() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setError(null);
    setDialogOpen(true);
  }

  function openEdit(row) {
    setEditing(row);
    setForm({
      name: row.name || "",
      category: row.category || "general",
      default_frequency_days: row.default_frequency_days || "",
      is_transfer_gage: !!row.is_transfer_gage,
      requires_external_lab: !!row.requires_external_lab,
      tolerance_text: row.tolerance_text || "",
      notes: row.notes || "",
    });
    setError(null);
    setDialogOpen(true);
  }

  async function handleSubmit() {
    setError(null);
    if (!form.name) return setError("Name required");
    setSaving(true);
    try {
      const payload = {
        ...form,
        default_frequency_days: form.default_frequency_days ? parseInt(form.default_frequency_days, 10) : null,
      };
      if (editing) {
        await calibrationInstrumentsApi.update(editing.id, payload);
        toast.success("Updated instrument type");
      } else {
        await calibrationInstrumentsApi.create(payload);
        toast.success("Created instrument type");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      const msg = err?.response?.data?.error || err.message;
      setError(msg);
      toast.error(msg);
    } finally { setSaving(false); }
  }

  async function handleDelete(row) {
    if (!window.confirm(`Soft-delete instrument type "${row.name}"? Existing units stay; new units cannot be added.`)) return;
    try {
      await calibrationInstrumentsApi.remove(row.id);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message);
    }
  }

  return (
    <Container maxWidth="xl">
      <Helmet><title>Calibration Catalog | SFM</title></Helmet>
      <Breadcrumbs sx={{ mb: 1 }}>
        <RouterLink to="/dashboard/app" style={{ color: "inherit" }}>Home</RouterLink>
        <RouterLink to="/calibration" style={{ color: "inherit" }}>Calibration</RouterLink>
        <Typography color="text.primary">Catalog</Typography>
      </Breadcrumbs>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h4">Instrument Catalog</Typography>
          <Typography variant="body2" color="text.secondary">
            Per-tenant configurable. SFM ships with no default catalog. Each tenant defines their own list.
          </Typography>
        </Box>
        {canManage && <Button variant="contained" onClick={openCreate}>+ Add Instrument Type</Button>}
      </Stack>
        <PageGuide
        why="Define the instrument types your shop uses — so the calibration register draws from a consistent list."
        who="The QM, defining the shop's instrument types."
        what="Your catalog of instrument types (gauges, meters, etc.) that the calibration register uses."
        how="Add instrument types here; the register then tracks individual units against them."
        when="When setting up calibration, or when you introduce a new kind of instrument."
        standard="AISC 207-25 §2.7"
        />

      <Alert severity="info" sx={{ mb: 2 }}>
        <strong>Tenant-flexibility canon:</strong> SFM does not seed a default catalog. ABC's auditor walks ABC's actual instrument list. Adding a type here is the prerequisite for adding instrument units to the register.
      </Alert>

      {loading ? <CircularProgress /> : (
        <TableContainer component={Paper}>
          <SmartTable tableId="calibration-catalog" lockedColumns={["Name"]} size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Default Frequency (days)</TableCell>
                <TableCell>Transfer Gage?</TableCell>
                <TableCell>External Lab?</TableCell>
                <TableCell>Tolerance</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow><TableCell colSpan={7} sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
                  No instrument types in catalog. Add one to start.
                </TableCell></TableRow>
              ) : rows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>{r.name}</TableCell>
                  <TableCell>{CATEGORY_LABELS[r.category] || r.category}</TableCell>
                  <TableCell>{r.default_frequency_days || "—"}</TableCell>
                  <TableCell>{r.is_transfer_gage ? "✓" : "—"}</TableCell>
                  <TableCell>{r.requires_external_lab ? "✓" : "—"}</TableCell>
                  <TableCell>{r.tolerance_text || "—"}</TableCell>
                  <TableCell align="right">
                    {canManage && (
                      <>
                        <IconButton size="small" aria-label="Edit" onClick={() => openEdit(r)}><EditIcon fontSize="small" /></IconButton>
                        <IconButton size="small" aria-label="Delete" onClick={() => handleDelete(r)}><DeleteIcon fontSize="small" /></IconButton>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </SmartTable>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? "Edit Instrument Type" : "Add Instrument Type"}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required fullWidth />
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select label="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                {CATEGORY_OPTIONS.map(([v, label]) => <MenuItem key={v} value={v}>{label}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField
              label="Default Frequency (days)"
              type="number"
              value={form.default_frequency_days}
              onChange={(e) => setForm({ ...form, default_frequency_days: e.target.value })}
              helperText="Leave blank for transfer gages (per-use validation, no due-date)"
              fullWidth
            />
            <FormControlLabel
              control={<Checkbox checked={form.is_transfer_gage} onChange={(e) => setForm({ ...form, is_transfer_gage: e.target.checked })} />}
              label="Transfer gage (per-use validation, no sticker)"
            />
            <FormControlLabel
              control={<Checkbox checked={form.requires_external_lab} onChange={(e) => setForm({ ...form, requires_external_lab: e.target.checked })} />}
              label="Requires external lab calibration"
            />
            <TextField label="Tolerance (free-text)" value={form.tolerance_text} onChange={(e) => setForm({ ...form, tolerance_text: e.target.value })} placeholder='e.g. ±1/16" at any interval' fullWidth />
            <TextField label="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} multiline rows={2} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={saving}>{saving ? "Saving..." : "Save"}</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
